'use client';

import Link from 'next/link';
import { BRAND_BLUE } from '@voley/shared-types/constants';

export default function ReleasingGuidePage() {
  return (
    <div className="bg-white">
      {/* Breadcrumb */}
      <div className="bg-white border-b border-gray-100 py-4">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex" aria-label="Breadcrumb">
            <ol className="flex items-center space-x-4">
              <li>
                <Link href="/docs" className="text-brand hover:text-[#2a6bb0] font-medium">
                  Docs &amp; Guides
                </Link>
              </li>
              <li>
                <div className="flex items-center">
                  <svg className="shrink-0 h-5 w-5 text-gray-300" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                    <path d="M5.555 17.776l8-16 .894.448-8 16-.894-.448z" />
                  </svg>
                  <span className="ml-4 text-sm font-medium text-gray-600">Releasing a Phone Number</span>
                </div>
              </li>
            </ol>
          </nav>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="lg:grid lg:grid-cols-4 lg:gap-12">

          {/* Table of Contents - Sidebar */}
          <div className="hidden lg:block lg:col-span-1">
            <div className="sticky top-8">
              <h3 className="text-sm font-semibold tracking-wide uppercase text-gray-400 mb-4">On this page</h3>
              <nav className="space-y-3 border-l-2 border-gray-100 pl-4">
                <a href="#overview" className="block text-sm text-gray-600 hover:text-brand transition-colors">Overview</a>
                <a href="#before-you-release" className="block text-sm text-gray-600 hover:text-brand transition-colors">Before You Release</a>
                <a href="#how-to-release" className="block text-sm text-gray-600 hover:text-brand transition-colors">How to Release</a>
                <a href="#what-happens" className="block text-sm text-gray-600 hover:text-brand transition-colors">What Happens Next</a>
                <a href="#hosted-numbers" className="block text-sm text-gray-600 hover:text-brand transition-colors">Hosted Numbers</a>
                <a href="#faq" className="block text-sm text-gray-600 hover:text-brand transition-colors">FAQ</a>
                <a href="#support" className="block text-sm text-gray-600 hover:text-brand transition-colors">Get Help</a>
              </nav>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            {/* Header */}
            <div className="mb-8">
              <h1 className="text-4xl font-bold text-gray-900 mb-4">Releasing a Phone Number</h1>
              <p className="text-xl text-gray-600">
                How to release a phone number from your Voley account, what happens when you do, and what to consider first.
              </p>
              <div className="mt-4 flex items-center text-sm text-gray-500">
                <span>Estimated time: 5 minutes</span>
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 mb-8 [&>:first-child]:mt-0">
              <p className="text-sm text-gray-700 mb-0">
                <strong>Important:</strong> Releasing a phone number is not reversible. Once released, the number returns to the carrier pool and may be reassigned to another business. Contact our support team to discuss your options before proceeding.
              </p>
            </div>

            {/* Content Sections */}
            <div className="prose prose-lg max-w-none">

              <section id="overview" className="mb-12">
                <h2 className="text-2xl font-semibold mb-4 text-gray-900">Overview</h2>
                <p className="text-gray-600 mb-4">
                  Releasing a phone number removes it from your Voley account. You might consider this if you&apos;re closing your account, switching to a different number, or no longer need SMS capabilities on a particular line.
                </p>
                <p className="text-gray-600">
                  To protect businesses from accidental releases, <strong>this is an assisted process</strong> handled by our support team. There is no self-serve release button — this is intentional to prevent accidental loss of a number your clients recognize.
                </p>
              </section>

              <section id="before-you-release" className="mb-12">
                <h2 className="text-2xl font-semibold mb-4 text-gray-900">Before You Release</h2>
                <p className="text-gray-600 mb-4">Consider these points carefully before requesting a release:</p>

                <div className="not-prose space-y-3 mb-6">
                  {[
                    {
                      icon: '⚠️',
                      label: 'You will lose the number permanently',
                      desc: 'Once released, the number goes back to the carrier pool. There is no guarantee you can get it back.',
                    },
                    {
                      icon: '💬',
                      label: 'Active conversations will be affected',
                      desc: 'Contacts who have your number saved will no longer be able to reach you via text at that number.',
                    },
                    {
                      icon: '📋',
                      label: 'Your 10DLC registration stays intact',
                      desc: 'Releasing a number does not cancel your 10DLC campaign. You can register a new number under the same campaign later.',
                    },
                    {
                      icon: '🔄',
                      label: 'Consider hosting reversal instead',
                      desc: 'If your number is hosted (not ported), we can simply remove SMS routing and return the number to your carrier — no release needed.',
                    },
                  ].map((item) => (
                    <div key={item.label} className="flex items-start gap-3 p-3 rounded-xl border border-gray-100">
                      <span className="text-lg mt-0.5 shrink-0">{item.icon}</span>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{item.label}</p>
                        <p className="text-xs text-gray-500 mt-0.5">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              <section id="how-to-release" className="mb-12">
                <h2 className="text-2xl font-semibold mb-4 text-gray-900">How to Release a Number</h2>
                <p className="text-gray-600 mb-4">
                  To release a number, contact our support team. We&apos;ll verify your identity, confirm the request, and process the release.
                </p>

                <div className="not-prose bg-gray-50 rounded-2xl px-5 py-4 mb-6">
                  <h3 className="text-base font-semibold text-gray-900 mb-4">Steps:</h3>
                  <ol className="space-y-4">
                    {[
                      {
                        title: 'Email support',
                        desc: 'Send a request to support@voley.us with your organization name and the phone number you want to release.',
                      },
                      {
                        title: 'Confirm your identity',
                        desc: 'A team member will verify you are an authorized administrator on the account.',
                      },
                      {
                        title: 'Review and confirm',
                        desc: 'We\'ll explain what happens to your active conversations and consent data, and ask you to confirm the release.',
                      },
                      {
                        title: 'Processing',
                        desc: 'We process the release with the carrier. This typically completes within 1–2 business days.',
                      },
                    ].map((step, i) => (
                      <li key={step.title} className="flex items-start gap-3">
                        <span className="flex items-center justify-center h-6 w-6 rounded-full bg-brand text-white text-xs font-medium shrink-0 mt-0.5">
                          {i + 1}
                        </span>
                        <div>
                          <p className="text-sm font-medium text-gray-900">{step.title}</p>
                          <p className="text-sm text-gray-600 mt-0.5">{step.desc}</p>
                        </div>
                      </li>
                    ))}
                  </ol>
                </div>
              </section>

              <section id="what-happens" className="mb-12">
                <h2 className="text-2xl font-semibold mb-4 text-gray-900">What Happens After Release</h2>
                <ul className="list-disc pl-6 text-gray-600 space-y-2">
                  <li>The number is removed from your Voley account immediately.</li>
                  <li>Existing conversation history is <strong>preserved</strong> in your account for your records.</li>
                  <li>Contacts can no longer text you at that number — messages will fail to deliver.</li>
                  <li>Your 10DLC campaign registration remains active and can be used with a new number.</li>
                  <li>Consent data (opt-in/opt-out records) is preserved for compliance purposes.</li>
                </ul>
              </section>

              <section id="hosted-numbers" className="mb-12">
                <h2 className="text-2xl font-semibold mb-4 text-gray-900">Hosted Numbers: A Better Option</h2>
                <p className="text-gray-600 mb-4">
                  If your number was <strong>hosted</strong> (SMS routing only, voice stays with your carrier), you don&apos;t need to release it. We can simply remove the SMS routing and return full control of the number to your existing carrier.
                </p>
                <p className="text-gray-600 mb-4">
                  This is non-destructive — you keep the number, and you can re-host it with Voley in the future if needed.
                </p>
                <div className="bg-brand/10 border border-brand/20 rounded-2xl p-5 [&>:first-child]:mt-0">
                  <p className="text-sm text-gray-700 mb-0">
                    <strong>Not sure if your number is hosted or ported?</strong> Check your <Link href="/settings?section=phone" className="text-brand hover:text-[#2a6bb0]">Phone Number settings</Link> or ask our support team.
                  </p>
                </div>
              </section>

              <section id="faq" className="mb-12">
                <h2 className="text-2xl font-semibold mb-4 text-gray-900">Frequently Asked Questions</h2>

                <h3 className="text-lg font-semibold mt-6 mb-2 text-gray-900">Why can&apos;t I release the number myself?</h3>
                <p className="text-gray-600 mb-4">
                  Releasing a number is irreversible and affects your ability to communicate with clients. The assisted process ensures you understand the implications and have a chance to explore alternatives (like hosting reversal) first.
                </p>

                <h3 className="text-lg font-semibold mt-6 mb-2 text-gray-900">Can I get my number back after releasing it?</h3>
                <p className="text-gray-600 mb-4">
                  Generally, no. Once released, the number returns to the carrier pool. In rare cases, if you act quickly, we may be able to recover it — but this is not guaranteed.
                </p>

                <h3 className="text-lg font-semibold mt-6 mb-2 text-gray-900">Will I lose my conversation history?</h3>
                <p className="text-gray-600 mb-4">
                  No. All conversation history, contact data, and consent records are preserved in your account even after releasing a number.
                </p>

                <h3 className="text-lg font-semibold mt-6 mb-2 text-gray-900">How long does it take?</h3>
                <p className="text-gray-600 mb-4">
                  Once confirmed, the release typically processes within 1&ndash;2 business days.
                </p>
              </section>

              <section id="support" className="mb-12">
                <h2 className="text-2xl font-semibold mb-4 text-gray-900">Get Help</h2>
                <div className="not-prose bg-white rounded-2xl px-5 py-4 border border-gray-200">
                  <p className="text-gray-600 mb-4">
                    Ready to release a number, or have questions about your options? Reach out and we&apos;ll help you figure out the best path.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <a
                      href="mailto:support@voley.us"
                      className="inline-flex items-center justify-center px-5 py-2.5 text-sm font-medium rounded-full text-white no-underline"
                      style={{ backgroundColor: BRAND_BLUE }}
                    >
                      Email support@voley.us
                    </a>
                    <Link
                      href="/contact"
                      className="inline-flex items-center justify-center px-5 py-2.5 text-sm font-medium rounded-full text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 no-underline"
                    >
                      Contact us
                    </Link>
                  </div>
                </div>
              </section>

            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
